import React, { useEffect, useState } from 'react';
import funcionalidadesService, { type FuncionalidadeDTO } from '../services/funcionalidadesService';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';
import { notifyError } from '../utils/notify';
import { toUpperText } from '../utils/formatters';

type FuncionalidadeForm = {
  chave: string;
  descricao: string;
  categoria: 'SAUDE' | 'EDUCACAO';
  percentual: string;
};

const EMPTY_FORM: FuncionalidadeForm = {
  chave: '',
  descricao: '',
  categoria: 'SAUDE',
  percentual: '',
};

const CATEGORY_OPTIONS: Array<{ value: FuncionalidadeForm['categoria']; label: string }> = [
  { value: 'SAUDE', label: 'Saúde' },
  { value: 'EDUCACAO', label: 'Educação' },
];

const categoryLabel = (categoria?: string) =>
  categoria === 'EDUCACAO' ? 'Educação' : 'Saúde';

const FuncionalidadesConfigPage: React.FC = () => {
  const [items, setItems] = useState<FuncionalidadeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FuncionalidadeDTO | null>(null);
  const [form, setForm] = useState<FuncionalidadeForm>(EMPTY_FORM);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = FIELD_CLASS;
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await funcionalidadesService.listAll();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      notifyError('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const totalPercentualAtivoAtual = items
    .filter((item) => item.ativo)
    .reduce((sum, item) => sum + Number(item.percentual || 0), 0);

  const totalPercentualProjetado = totalPercentualAtivoAtual
    - (editing?.ativo ? Number(editing.percentual || 0) : 0)
    + Number(form.percentual || 0);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (totalPercentualProjetado > 100) {
      notifyError('A soma dos percentuais ativos não pode ultrapassar 100%');
      return;
    }

    try {
      const payload = {
        chave: form.chave.trim(),
        descricao: form.descricao.trim(),
        categoria: form.categoria,
        percentual: Number(form.percentual),
      };

      if (editing) {
        await funcionalidadesService.update(editing.id, {
          descricao: payload.descricao,
          categoria: payload.categoria,
          percentual: payload.percentual,
        });
      } else {
        await funcionalidadesService.create(payload);
      }

      await fetchItems();
      resetForm();
    } catch (err: any) {
      notifyError(err?.response?.data?.error || 'Erro ao salvar');
    }
  };

  const startEdit = (item: FuncionalidadeDTO) => {
    setEditing(item);
    setForm({
      chave: item.chave,
      descricao: item.descricao || '',
      categoria: item.categoria,
      percentual: String(item.percentual ?? ''),
    });
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir regra?')) return;
    try {
      await funcionalidadesService.remove(id);
      await fetchItems();
      if (editing?.id === id) resetForm();
    } catch {
      notifyError('Erro ao excluir');
    }
  };

  const summaryCards = [
    { label: 'Regras cadastradas', value: items.length, dot: 'bg-slate-400' },
    { label: 'Regras ativas', value: items.filter((item) => item.ativo).length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Percentual ativo', value: `${Number(totalPercentualAtivoAtual).toFixed(2)}%`, dot: 'bg-emerald-500' },
    { label: 'Percentual projetado', value: `${Number(totalPercentualProjetado || 0).toFixed(2)}%`, dot: totalPercentualProjetado > 100 ? 'bg-red-500' : 'bg-amber-500' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Configurações' : 'Administrador'}
        </p>
        <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Funcionalidades</h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
              Cadastre uma regra por linha usando a mesma chave da função ou área de atuação da emenda.
            </p>
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Função / área de atuação</label>
            <input
              required
              disabled={!!editing}
              value={form.chave}
              onChange={(e) => setForm((current) => ({ ...current, chave: toUpperText(e.target.value) }))}
              className={inputClass}
              placeholder="Código da função / área"
            />
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Descrição</label>
            <input
              value={form.descricao}
              onChange={(e) => setForm((current) => ({ ...current, descricao: toUpperText(e.target.value) }))}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Categoria</label>
            <select
              value={form.categoria}
              onChange={(e) => setForm((current) => ({ ...current, categoria: e.target.value as FuncionalidadeForm['categoria'] }))}
              className={inputClass}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Percentual</label>
            <input
              required
              min="0"
              max="100"
              type="number"
              step="0.01"
              value={form.percentual}
              onChange={(e) => setForm((current) => ({ ...current, percentual: e.target.value }))}
              className={`${inputClass} max-w-[14rem]`}
            />
          </div>

          <div className="flex gap-2 pt-1 md:col-span-4">
            <button
              type="submit"
              className={`${primaryButtonClass}`}
            >
              {editing ? 'Salvar' : 'Criar'}
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className={`${secondaryButtonClass}`}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
        <div className="overflow-x-auto">
          <Table density="comfortable">
            <Thead>
              <Tr>
                <Th className={labelTextClass}>
                  Função / área
                </Th>
                <Th className={labelTextClass}>
                  Descrição
                </Th>
                <Th className={labelTextClass}>
                  Categoria
                </Th>
                <Th align="center" className={labelTextClass}>
                  Percentual
                </Th>
                <Th align="center" className={labelTextClass}>
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td align="center" colSpan={5} className={`text-sm ${labelTextClass}`}>
                    Carregando regras...
                  </Td>
                </Tr>
              ) : items.length === 0 ? (
                <Tr>
                  <Td align="center" colSpan={5} className={`text-sm ${labelTextClass}`}>
                    Nenhuma regra cadastrada.
                  </Td>
                </Tr>
              ) : (
                items.map((item) => (
                  <Tr key={item.id}>
                    <Td className={`text-sm font-medium ${strongTextClass}`}>{item.chave}</Td>
                    <Td className={`text-sm ${bodyTextClass}`}>{item.descricao || '-'}</Td>
                    <Td className={`text-sm ${bodyTextClass}`}>{categoryLabel(item.categoria)}</Td>
                    <Td align="center" className={`text-sm ${strongTextClass}`}>
                      {Number(item.percentual || 0).toFixed(2)}%
                    </Td>
                    <Td align="center" className="text-sm">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className={`mr-3 font-medium ${accentTextClass}`}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="font-medium text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200"
                      >
                        Excluir
                      </button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </div>
      </section>
    </div>
  );
};

export default FuncionalidadesConfigPage;
